/**
 * Base products (Grundprodukte, docs/18-vorratsbuch.md, Katalog): the entries of the shared product
 * catalogue, at the level one writes on a list ("Dosentomaten"). Every article of VorratsBuch
 * points to one; the links to EinkaufsZettel and SpeisePlan speak in them. The catalogue, its search
 * and lookup come from household-kit (`@household-kit/food`, a checked copy in packages/shared/food,
 * F15); here only what VorratsBuch adds: which aisles it shows, the unit a new article starts with.
 */
import { AISLES, PRODUCTS, aisle as foodAisle, aislesOf, product, productId, search } from "@household-kit/food";
import type { Aisle as FoodAisle, Product } from "@household-kit/food";
import { fold } from "./search.ts";

export type Aisle = FoodAisle;
export type BaseProduct = Product;

export { AISLES };
export const BASE_PRODUCTS: readonly BaseProduct[] = PRODUCTS;

/** Drinks belong to KellerLog (F18); VorratsBuch leaves this aisle out. */
export const DRINKS_AISLE = "getraenke";

/** Aisles of household goods rather than food (profile Haushalt): those of kind „household“ in food. */
export const HOUSEHOLD_AISLES: readonly string[] = aislesOf("household").map((a) => a.id);

/** The id a name gets ("Rote Zwiebel" → "rote-zwiebel"), as food gives it. */
export const baseProductId = (name: string) => productId(name);

export const baseProduct = (id: unknown): BaseProduct | undefined => product(id);
export const aisleOf = (id: unknown): Aisle | undefined => foodAisle(id);

/**
 * The picture of a pantry article, as the shopping list shows it (`emojiFor` there): the base
 * product's emoji, else its aisle's, else the aisle named at the article, else a parcel.
 */
export function emojiOf(f: Record<string, unknown>): string {
  const p = baseProduct(f.base);
  if (p?.emoji) return p.emoji;
  const a = aisleOf(p?.aisle) ?? AISLES.find((x) => x.name === f.aisle);
  return a?.emoji ?? "📦";
}

/** The aisles of VorratsBuch, in the order of the walk through the shop. */
export const PANTRY_AISLES: readonly Aisle[] = aislesOf("food", "household");

/**
 * Base products that fit a query, best first (food's search: typos forgiven, compounds and packing
 * in the name understood, „Passata“ finds Passierte Tomaten). Drinks only with `drinks`.
 */
export function searchBaseProducts(query: string, limit = 8, opts: { drinks?: boolean } = {}): BaseProduct[] {
  if (query.trim() === "") return [];
  return search(query, { limit, ...(opts.drinks ? {} : { kinds: ["food", "household"] }) }).map((h) => h.product);
}

/**
 * The base unit a new article of a base product starts with (V0-2): what food says for liquids (ml:
 * milk, cream, oil, vinegar; A3), Stk. for household goods, else g.
 */
export function suggestedUnit(p: BaseProduct | undefined): "g" | "ml" | "Stk." {
  if (!p) return "Stk.";
  if (p.unit === "ml") return "ml";
  if (HOUSEHOLD_AISLES.includes(p.aisle)) return "Stk.";
  if (p.aisle === DRINKS_AISLE) return "ml";
  // Fruit and vegetables are mostly counted (Äpfel, Gurken); what comes loose by weight stays g.
  if (p.aisle === "obst" && !/kartoffel|zwiebel|karotte|moehre|tomate|beere|traube|kirsche|pilz|champignon|salat|spinat|bohne|erbse/.test(fold(p.names[0]!))) return "Stk.";
  return "g";
}

/** Aisles whose goods go into the fridge; fruit and vegetables are left open (potatoes and bananas are not kept cool). */
const COOL_AISLES = ["milch", "kaese", "fleisch", "fisch", "kuehl"];

/**
 * How a food is kept, as its aisle suggests (C3): frozen goods „tiefgekühlt“, dairy, cheese, meat,
 * fish and the chilled shelf „kühl“, the dry aisles „trocken“. Nothing for fruit and vegetables and
 * for household goods.
 */
export function suggestedKeep(p: BaseProduct | undefined): "trocken" | "kühl" | "tiefgekühlt" | undefined {
  if (!p || p.aisle === "obst" || p.aisle === DRINKS_AISLE || HOUSEHOLD_AISLES.includes(p.aisle)) return undefined;
  if (p.aisle === "tk") return "tiefgekühlt";
  return COOL_AISLES.includes(p.aisle) ? "kühl" : "trocken";
}
